import React from 'react';
import { useTranslation } from 'react-i18next';
import { Plane, Map, CheckCircle } from 'lucide-react';
import { Card, CardContent } from './ui/card';

const SERVICES = [
  {
    id: 1,
    iconKey: 'Plane',
    titleKey: 'services.transfer_title',
    descKey: 'services.transfer_description',
    featuresKey: 'services.transfer_features',
    image: 'https://images.unsplash.com/photo-1530521954074-e64f6810b32d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxhaXJwb3J0JTIwdHJhbnNmZXJ8ZW58MHx8fHwxNzc1ODA1OTE1fDA&ixlib=rb-4.1.0&q=85'
  },
  {
    id: 2,
    iconKey: 'Map',
    titleKey: 'services.tour_title',
    descKey: 'services.tour_description',
    featuresKey: 'services.tour_features',
    image: 'https://images.unsplash.com/photo-1616607006500-b08d26749c64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MTN8MHwxfHNlYXJjaHwxfHxwb3J0dWdhbCUyMHRvdXJpc218ZW58MHx8fHwxNzc1ODA1OTExfDA&ixlib=rb-4.1.0&q=85'
  }
];

const iconMap = {
  Plane: Plane,
  Map: Map
};

export const Services = () => {
  const { t } = useTranslation();

  return (
    <section id="services" className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="services-title">
            {t('services.title')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('services.subtitle')}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {SERVICES.map((service) => {
            const IconComponent = iconMap[service.iconKey];
            const features = t(service.featuresKey, { returnObjects: true });
            return (
              <Card 
                key={service.id} 
                className="group hover:shadow-2xl transition-all duration-300 border-0 overflow-hidden"
                data-testid={`service-card-${service.id}`}
              >
                {/* Service Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={t(service.titleKey)}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  
                  {/* Icon */}
                  <div className="absolute top-6 left-6 bg-amber-600 rounded-2xl p-4 shadow-lg">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                </div>

                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {t(service.titleKey)}
                  </h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {t(service.descKey)}
                  </p>

                  {/* Features List */}
                  <div className="space-y-3">
                    {Object.values(features || {}).map((feature, index) => (
                      <div key={index} className="flex items-center space-x-3">
                        <CheckCircle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};
